import type { StakingPool } from 'api/StakingAPI';

export type StakingPoolLiveInput = {
  balance: bigint;
  earlyUnlockFees: bigint;
  tokenDecimals: number;
  tokenPriceUsd: number;
};

export type StakingPoolValue = StakingPoolLiveInput & {
  isLive: boolean;
  principalBalance: bigint;
  usdValue: number;
};

export const getStakingPoolValueKey = (
  pool: Pick<StakingPool, 'chainId' | 'stakingContractAddress'>
) => `${pool.chainId}:${pool.stakingContractAddress.toLowerCase()}`;

const backendPoolValue = (pool: Pick<StakingPool, 'totalValueLockedUSD'>) => {
  const value = Number(pool.totalValueLockedUSD);
  return Number.isFinite(value) ? value : 0;
};

export const formatStakingTokenUnits = (value: bigint, decimals: number) => {
  if (decimals === 0) return value.toString();

  const raw = value.toString().padStart(decimals + 1, '0');
  const decimalIndex = raw.length - decimals;
  const fraction = raw.slice(decimalIndex).replace(/0+$/, '');

  return fraction
    ? `${raw.slice(0, decimalIndex)}.${fraction}`
    : raw.slice(0, decimalIndex);
};

export const subtractEarlyUnlockFees = (balance: bigint, fees: bigint) =>
  balance > fees ? balance - fees : BigInt(0);

export const resolveStakingPoolValue = (
  pool: Pick<StakingPool, 'totalValueLockedUSD'>,
  liveInput?: StakingPoolLiveInput
): StakingPoolValue => {
  const fallback = backendPoolValue(pool);
  const hasValidInput =
    liveInput !== undefined &&
    liveInput.balance >= liveInput.earlyUnlockFees &&
    Number.isFinite(liveInput.tokenPriceUsd) &&
    liveInput.tokenPriceUsd > 0;

  if (!hasValidInput) {
    return {
      balance: liveInput?.balance ?? BigInt(0),
      earlyUnlockFees: liveInput?.earlyUnlockFees ?? BigInt(0),
      tokenDecimals: liveInput?.tokenDecimals ?? 18,
      tokenPriceUsd: liveInput?.tokenPriceUsd ?? 0,
      isLive: false,
      principalBalance: BigInt(0),
      usdValue: fallback,
    };
  }

  const principalBalance = subtractEarlyUnlockFees(
    liveInput.balance,
    liveInput.earlyUnlockFees
  );
  const tokenAmount = Number(
    formatStakingTokenUnits(principalBalance, liveInput.tokenDecimals)
  );
  const usdValue = tokenAmount * liveInput.tokenPriceUsd;

  if (!Number.isFinite(usdValue)) {
    return {
      ...liveInput,
      isLive: false,
      principalBalance,
      usdValue: fallback,
    };
  }

  return { ...liveInput, isLive: true, principalBalance, usdValue };
};

export const applyStakingPoolValues = (
  pools: readonly StakingPool[],
  values: Readonly<Record<string, StakingPoolValue>>
): StakingPool[] =>
  pools.map((pool) => {
    const value = values[getStakingPoolValueKey(pool)];
    if (!value) return pool;

    return {
      ...pool,
      tokensStaked: value.isLive
        ? formatStakingTokenUnits(
            value.principalBalance,
            pool.depositTokenDecimals
          )
        : pool.tokensStaked,
      totalValueLockedUSD: String(value.usdValue),
    };
  });

export const sumResolvedStakingPoolValues = (
  pools: readonly StakingPool[],
  values: Readonly<Record<string, StakingPoolValue>>
) =>
  pools.reduce(
    (total, pool) =>
      total +
      (values[getStakingPoolValueKey(pool)]?.usdValue ??
        backendPoolValue(pool)),
    0
  );
